/**
 * BCH (15,5) error correction for the format information of the QR Code;
 * BCH (18,6) error correction for the version information of the QR Code;
 * this is used for error correction in the format information of the QR Code;
 */

/** Generator polynomial for format string according to QR Code specification*/
export const FORMAT_STRING_GENERATOR_POLYNOMIAL = 0b10100110111;

/** Generator polynomial for version string to QR Code specification */
export const VERSION_STRING_GENERATOR_POLYNOMIAL = 0b1111100100101;

/** format string mask according to QR Code specification */
export const FORMAT_STRING_MASK = 0b101010000010010;

/**
 * The low level function to generate BCH code for the format information of the QR Code.
 * @param {number} data - The data to be encoded
 * @param {number} generator - The generator polynomial for the BCH code
 * @returns {number} - The BCH error correction code
 */
export function BCHCode(data, generator) {
  let remainder = data;
  const generatorLength = generator.toString(2).length;
  while (remainder.toString(2).length >= generatorLength) {
    const shift = remainder.toString(2).length - generatorLength;
    remainder = remainder ^ (generator << shift);
  }
  return remainder;
}

/**
 * Reed Solomon Error Correction Algorithm implmented for Galois Field 256 (GF256) irreducible polynomial 0x11d (285) according to the standard of QR Code.
 */

const GF256 = (() => {
  const prim = 0x11d; // Primitive polynomial for GF(2^8)
  const log = new Uint8Array(256);
  const exp = new Uint8Array(512);
  let x = 1;
  for (let i = 0; i < 255; i++) {
    // console.log("i", i, "x", x);
    exp[i] = x;
    log[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= prim;
  }
  for (let i = 255; i < 512; i++) exp[i] = exp[i - 255];
  return { log, exp, mul: (a, b) => (a && b ? exp[log[a] + log[b]] : 0) };
})();

function generateGenerator(degree) {
  console.assert(degree >= 1, 'Degree must be at least 1');
  let gen = [1];
  for (let i = 0; i < degree; i++) {
    let term = [1, GF256.exp[i]];
    gen = polyMul(gen, term);
  }
  return gen;
}

function polyMul(a, b) {
  let result = new Array(a.length + b.length - 1).fill(0);
  for (let i = 0; i < a.length; i++) {
    for (let j = 0; j < b.length; j++) {
      result[i + j] ^= GF256.mul(a[i], b[j]);
    }
  }
  return result;
}

function polyDiv(dividend, divisor) {
  dividend = [...dividend];
  for (let i = 0; i < dividend.length - (divisor.length - 1); i++) {
    let coef = dividend[i];
    if (coef !== 0) {
      for (let j = 1; j < divisor.length; j++) {
        dividend[i + j] ^= GF256.mul(divisor[j], coef);
      }
    }
  }
  return dividend.slice(-(divisor.length - 1));
}

// Example usage:
// const generator = generateGenerator(2);
// console.log("Encoded message:", encoded);
/**
 * The function to encode the Reed Solomon error correction for the message.
 * @param {number} eccLength - The number of error correction terms to be added to the message
 * @example <caption> Example Usage of the encodeReedSolomon function </caption>
 * const message = [32, 91, 11, 120, 209]; // 5-term message
 * const eccLength = 7; // 2 error correction terms
 * const encoded = encodeReedSolomon(eccLength)(message);
 * @returns {function(Iterable<number>):Uint8Array} The function to encode the message
 */
export const encodeReedSolomon = (eccLength) => (message) => {
  let generator = generateGenerator(eccLength);
  let paddedMessage = [...message, ...new Array(eccLength).fill(0)];
  let remainder = polyDiv(paddedMessage, generator);
  return new Uint8Array(remainder);
};
